import { useSearchParams } from "react-router-dom";
import { driver } from "driver.js"; // Assuming you're using driver.js for the tour
import "driver.js/dist/driver.css";

const useTour = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const getTableTitle = (type: string | null): string => {
    switch (type) {
      case "Animals":
        return "Animal Data Table";
      case "Farms":
        return "Farm Data Table";
      case "Supervisors":
        return "Supervisor Data Table";
      case "Farmers":
        return "Farmer Data Table";
      case "New Farmers":
        return "New Farmers Data Table";
      case "Phenotype":
        return "Phenotype Data Table";
      case "DataVisualizer":
        return "Data Visualization Dashboard";
      default:
        return "Data Table";
    }
  };

  const getTableDescription = (type: string | null): string => {
    switch (type) {
      case "Animals":
        return "This interactive table provides a detailed overview of all animals in the system. You can filter, sort, and search for specific animals.";
      case "Farms":
        return "This table displays comprehensive details about farms, including location, size, and associated animals. Use it to manage farm-related data efficiently.";
      case "Supervisors":
        return "This table contains information about supervisors, including their profiles and assigned farms or animals. Use it to manage supervisor data.";
      case "Farmers":
        return "This table provides a detailed view of all farmers in the system, including their personal details and farm associations. Use it to manage farmer records.";
      case "New Farmers":
        return "This table displays newly registered farmers awaiting approval. Review their details and integrate them into the system.";
      case "Phenotype":
        return "This table provides detailed phenotypic data, including traits, characteristics, and genetic information for animals. Use it to analyze and manage phenotypic records.";
      case "DataVisualizer":
        return "This dashboard provides visual insights into your data, including charts, graphs, and analytics. Use it to monitor trends and make informed decisions.";
      default:
        return "This table displays relevant data based on the selected tab. Use it to view and manage information efficiently.";
    }
  };

  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      steps: [
        {
          element: "#Animals",
          popover: {
            title: "Animal Management",
            description:
              "This section allows you to manage all animal-related data, including details about breeds, health records, and ownership. Use this tab to view, add, or update animal information efficiently.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#Farms",
          popover: {
            title: "Farm Management",
            description:
              "Access and manage comprehensive details about farms, including location, size, and associated animals. This tab helps you organize farm data and monitor agricultural activities.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#Supervisors",
          popover: {
            title: "Supervisor Management",
            description:
              "This section is dedicated to managing supervisor profiles. You can register new supervisors, update their details, and assign them to specific farms or animals for monitoring purposes.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#Farmers",
          popover: {
            title: "Farmer Management",
            description:
              "Manage all farmer-related information, including personal details, farm associations, and animal ownership. This tab helps you maintain an up-to-date record of farmers in the system.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#New Farmers",
          popover: {
            title: "New Farmer Management",
            description:
              "This section is dedicated to managing newly registered farmers. Review and approve their profiles to integrate them into the system.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#avatarOption",
          popover: {
            title: "sidebar",
            description:
              "This section is dedicated to managing newly registered farmers. Review and approve their profiles to integrate them into the system.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#Phenotype",
          popover: {
            title: "Phenotype Management",
            description:
              "This section allows you to manage phenotypic data, including traits, characteristics, and genetic information for animals.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: "#DataVisualizer",
          popover: {
            title: "Data Visualization Dashboard",
            description:
              "This dashboard provides visual insights into your data, including charts, graphs, and analytics. Use it to monitor trends and make informed decisions.",
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
        {
          element: `#AnimalsContent`, // Dynamic table ID
          popover: {
            title: getTableTitle(type),
            description: getTableDescription(type),
            onNextClick: () => {
              driverObj.moveNext();
            },
          },
        },
      ],
    });

    setTimeout(() => {
      driverObj.drive();
    }, 500);
  };

  return { getTableTitle, getTableDescription, startTour };
};

export default useTour;
